import type { ReactNode } from 'react';
import { Header, type EyebrowTone, type TitleAccent } from './_shared';

export type ColumnsItem = {
  /** Ordinal like "01" rendered above the name in mono. */
  num?: string;
  /** Leading icon (React node). */
  icon?: ReactNode;
  name: string;
  desc?: string;
  /** Optional bottom art (image src). */
  img?: string;
};

export type ColumnsSlideProps = {
  eyebrow: ReactNode;
  eyebrowTone?: EyebrowTone;
  title?: ReactNode;
  titleAccent?: TitleAccent;
  sub?: ReactNode;
  /** Visual treatment for each column. Default 'cards' (white card behind each). */
  variant?: 'columns' | 'cards' | 'cards-mini';
  /** Wrap each column icon in a Blue-01 rounded tile (DS Cards › IconBlock). */
  iconBox?: boolean;
  /** Grid column count. Defaults to columns.length, clamped 2..5. */
  cols?: 2 | 3 | 4 | 5;
  columns: ReadonlyArray<ColumnsItem>;
  /** Optional extra class on the slide-body root, for deck-local scoping. */
  className?: string;
};

function clampCols(n: number): 2 | 3 | 4 | 5 {
  if (n <= 2) return 2;
  if (n === 3) return 3;
  if (n === 4) return 4;
  return 5;
}

export function ColumnsSlide({
  eyebrow,
  eyebrowTone,
  title,
  titleAccent,
  sub,
  variant = 'cards',
  iconBox = false,
  cols,
  columns,
  className,
}: ColumnsSlideProps) {
  const colCount = cols ?? clampCols(columns.length);
  const baseClass =
    variant === 'cards-mini' ? 'sn-col sn-col--card-mini'
      : variant === 'cards' ? 'sn-col sn-col--card'
        : 'sn-col';
  const colClass = `${baseClass}${iconBox ? ' sn-col--icon-box' : ''}`;

  return (
    <div className={`slide-body${className ? ` ${className}` : ''}`}>
      <Header
        eyebrow={eyebrow}
        eyebrowTone={eyebrowTone}
        title={title}
        titleAccent={titleAccent}
        sub={sub}
      />
      <div className="sn-work">
        <div className={`sn-col-grid sn-col-grid--${colCount}`}>
          {columns.map((c, i) => (
            <article key={i} className={colClass}>
              {c.num && <span className="sn-col-num">{c.num}</span>}
              {c.icon && <span className="sn-col-icon">{c.icon}</span>}
              <h3 className="sn-col-name">{c.name}</h3>
              {c.desc && <p className="sn-col-desc">{c.desc}</p>}
              {c.img && (
                <div className="sn-col-img">
                  <img src={c.img} alt={c.name} />
                </div>
              )}
            </article>
          ))}
        </div>
      </div>
    </div>
  );
}
